import type {
  FacturaVenta,
  LibroIvaVentasSubtotalProvincia
} from '../services/facturaVentaService';

export const SIN_PROVINCIA_LABEL = 'Sin provincia';
export const SIN_PROVINCIA_KEY = 'sin';

export const claveProvincia = (codigoProvincia?: number | null): string =>
  codigoProvincia == null ? SIN_PROVINCIA_KEY : String(codigoProvincia);

export const nombreProvinciaSubtotal = (subtotal: LibroIvaVentasSubtotalProvincia): string =>
  (subtotal.nombreProvincia ?? '').trim() || SIN_PROVINCIA_LABEL;

/** Lista vacía = sin filtro (todas las provincias). */
export const provinciaEnFiltro = (codigoProvincia: number | null | undefined, filtro: string[]): boolean =>
  filtro.length === 0 || filtro.includes(claveProvincia(codigoProvincia));

export const ordenarSubtotalesProvincia = (
  subtotales: LibroIvaVentasSubtotalProvincia[]
): LibroIvaVentasSubtotalProvincia[] =>
  [...subtotales].sort((a, b) => {
    const nombreA = (a.nombreProvincia ?? '').trim();
    const nombreB = (b.nombreProvincia ?? '').trim();
    if (!nombreA && nombreB) return 1;
    if (nombreA && !nombreB) return -1;
    return nombreA.localeCompare(nombreB, 'es');
  });

/** Agrupa comprobantes por provincia cuando la API no envía los subtotales. */
export const agruparSubtotalesPorProvincia = (
  facturas: FacturaVenta[]
): LibroIvaVentasSubtotalProvincia[] => {
  const grupos = new Map<string, LibroIvaVentasSubtotalProvincia>();

  facturas.forEach((factura) => {
    const key = `${factura.codigoProvincia ?? ''}|${(factura.nombreProvincia ?? '').trim()}`;
    const prev = grupos.get(key) ?? {
      codigoProvincia: factura.codigoProvincia ?? null,
      nombreProvincia: factura.nombreProvincia ?? null,
      cantidadComprobantes: 0,
      totalImporteSinIva: 0,
      totalExento: 0,
      totalIvainscripto: 0,
      totalIvanoInscripto: 0,
      totalIvaotro: 0,
      totalImporteTotal: 0
    };
    grupos.set(key, {
      ...prev,
      cantidadComprobantes: prev.cantidadComprobantes + 1,
      totalImporteSinIva: prev.totalImporteSinIva + (factura.importeSinIva ?? 0),
      totalExento: prev.totalExento + (factura.exento ?? 0),
      totalIvainscripto: prev.totalIvainscripto + (factura.ivainscripto ?? 0),
      totalIvanoInscripto: prev.totalIvanoInscripto + (factura.ivanoInscripto ?? 0),
      totalIvaotro: prev.totalIvaotro + (factura.ivaotro ?? 0),
      totalImporteTotal: prev.totalImporteTotal + (factura.importeTotal ?? 0)
    });
  });

  return ordenarSubtotalesProvincia(Array.from(grupos.values()));
};

export const sumarSubtotalesProvincia = (
  subtotales: LibroIvaVentasSubtotalProvincia[]
): Omit<LibroIvaVentasSubtotalProvincia, 'codigoProvincia' | 'nombreProvincia'> =>
  subtotales.reduce(
    (acc, s) => ({
      cantidadComprobantes: acc.cantidadComprobantes + (s.cantidadComprobantes ?? 0),
      totalImporteSinIva: acc.totalImporteSinIva + (s.totalImporteSinIva ?? 0),
      totalExento: acc.totalExento + (s.totalExento ?? 0),
      totalIvainscripto: acc.totalIvainscripto + (s.totalIvainscripto ?? 0),
      totalIvanoInscripto: acc.totalIvanoInscripto + (s.totalIvanoInscripto ?? 0),
      totalIvaotro: acc.totalIvaotro + (s.totalIvaotro ?? 0),
      totalImporteTotal: acc.totalImporteTotal + (s.totalImporteTotal ?? 0)
    }),
    {
      cantidadComprobantes: 0,
      totalImporteSinIva: 0,
      totalExento: 0,
      totalIvainscripto: 0,
      totalIvanoInscripto: 0,
      totalIvaotro: 0,
      totalImporteTotal: 0
    }
  );
